import content from "./content";
import { answered } from "./loader";
import widgets from "./widgets";

/**
 * @typedef component
 * @type {object}
 * @property {string} name
 * @property {number} count
 * @property {properties=} props
 * @property {object=} dataSet
 * @property {definition[]=} children
 */

/**
 * Represents a single item within a page
 * @typedef definition
 * @type {object}
 * @property {string} t - type of component
 * @property {data} d - data for the component
 * @property {properties} p - properties of the component
 * @property {definition[]} c - child definitions
 * @property {reference} r - reference file
 */

/**
 * Represents the data configuration of a component
 * @typedef data
 * @type {object}
 * @property {string} v - name of the data view
 * @property {string=} f - the field of its row the part shows as its parts, a rich text
 */

/**
 * A reference file for the component type
 * @typedef reference
 * @property {string} name
 * @property {string} val
 */

/**
 * @typedef properties
 * @type {object}
 * @property {string} txt - text to display inside the component
 * @property {string} name - name to display for debugging
 * @property {string=} styles - the classes the component wears
 * @property {string=} width - how wide a viewport lays the page out on a phone, in pixels
 * @property {string=} below - the window width, in pixels, below which a call of the menu shows its button in place of its row
 * @property {string=} drop - how far below its button the menu's panel starts, in pixels
 * @property {string=} panel - the classes the menu's panel wears
 * @property {string=} id - the name a part goes by on the page
 * @property {string=} popover - how a part shown over the page closes: auto, on a tap elsewhere or Escape too
 * @property {string=} opens - the name of the part shown over the page a button opens and closes
 * @property {string=} label - what a part is called for a visitor who cannot see it
 * @property {string=} kind - what kind of control a field or button is
 * @property {track[]=} cols - a grid's columns and gaps, per page width
 * @property {string=} align - how a grid's cells sit up and down in their rows: start, end, center, stretch or baseline
 * @property {string=} justify - how a grid's columns sit across it: start, end, center, stretch or space-between, -around, -evenly
 * @property {(cell | cell[])=} cell - where a grid's child sits, for every width or per page width
 */

/**
 * A grid's columns and gaps from a page width up to below the next entry's
 * @typedef track
 * @type {object}
 * @property {number | string=} from - the least page width, in pixels, the entry holds from; none is 0
 * @property {string=} cols - the columns, as grid-template-columns writes them
 * @property {string=} gap - the space between rows then columns, as gap writes it
 */

/**
 * Where a grid's child sits from a page width up to below the next entry's; cells sharing an area are layered, a later child in front
 * @typedef cell
 * @type {object}
 * @property {number | string=} from - the least page width, in pixels, the entry holds from; none is 0
 * @property {number | string=} col - the column the cell starts at, the first 1
 * @property {number | string=} row - the row the cell sits in, the first 1
 * @property {number | string=} span - how many columns the cell spans
 * @property {number | string=} layer - how far in front the cell sits among those sharing its area, higher in front
 */

/**
 * Fetches a definition from the API
 * @param {string} id - identifier for the definition
 * @returns {Promise<definition[]>}
 */
async function fetchDefinitions(id) {
  return answered(fetch(`/api/page/${id}`));
}

const definition = {
  /**
   * Loads a definition, the site's facts it shows, and all the referenced widgets
   * and content into stores for future loading
   * @param {string} id - identifier for the definition
   * @returns {Promise<definition[]>} - a list of definitions which define the component
   */
  load: async (id) => {
    content.forgetFacts();
    const defs = await fetchDefinitions(id);
    await content.loadFacts(defs);
    await content.loadIn(defs);
    // A widget is drawn once the facts it shows are in, its rows asked for as the page's are.
    await widgets.load(defs, async (widgetDefs) => {
      await content.loadFacts(widgetDefs);
      content.loadIn(widgetDefs);
    });
    return defs;
  },
};

export default definition;
